import { useState } from 'react'

import { useCreateManualRequestApi, useMailQueueApi } from '@/common/api/modules/ugcMail'
import { useReviewTemplatesApi } from '@/common/api/modules/ugcReviewSetup'
import { ManualRequestResponse } from '@/common/api/params/ugcMail'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'

/**
 * 個別のレビュー依頼。
 *
 * 自動生成の対象にならない相手（受注がW2に無い、店頭で渡した等）に
 * 依頼したいときに使う。作られるものは自動生成と同じで、送信も同じ経路を通る。
 * 手動だけ別の送り方にすると、文面や差出人が片方だけ古いままになる。
 *
 * ただし**一覧では「個別に依頼」と分けて見えるようにしている**。自動配信の
 * 開封率に手動分が混ざると、その数字が何を表しているのか分からなくなる。
 *
 * 送信モードが「送らない」のときはメールを作らず、投稿フォームのURLだけを返す。
 * URLを直接お渡しする使い方もできる。
 */

const input =
  'shadow-inset text-size-13 w-full rounded-lg border border-border-input bg-input px-3 py-2'

type Form = {
  customer_email: string
  customer_name: string
  product_external_id: string
  product_name: string
  order_id: string
  template_id: string
}

const emptyForm = (): Form => ({
  customer_email: '',
  customer_name: '',
  product_external_id: '',
  product_name: '',
  order_id: '',
  template_id: ''
})

const ManualRequestPanel = ({ siteId }: { siteId: number }) => {
  const [form, setForm] = useState<Form>(emptyForm())
  const [result, setResult] = useState<ManualRequestResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const { data: templateData } = useReviewTemplatesApi({ site_id: siteId })
  const { mutate: refreshQueue } = useMailQueueApi({
    site_id: siteId,
    status: 'pending',
    limit: 50,
    offset: 0
  })

  const { trigger, isMutating } = useCreateManualRequestApi({
    onSuccess: (r) => {
      setResult(r)
      setError(null)
      setForm(emptyForm())
      refreshQueue()
    },
    onError: (e) => {
      // すでに同じ宛先・同じ商品へ依頼済みのときはサーバが理由を返す。
      // 握りつぶすと二重送信を止めた理由が伝わらない
      setResult(null)
      setError(e instanceof Error ? e.message : '依頼を作成できませんでした')
    }
  })

  const patch = (p: Partial<Form>) => setForm((f) => ({ ...f, ...p }))

  const onSubmit = () => {
    const email = form.customer_email.trim()
    if (!email || !email.includes('@')) {
      setError('送信先のメールアドレスを入力してください')
      setResult(null)
      return
    }
    setError(null)
    trigger({
      site_id: siteId,
      customer_email: email,
      customer_name: form.customer_name.trim() || null,
      product_external_id: form.product_external_id.trim() || null,
      product_name: form.product_name.trim() || null,
      order_id: form.order_id.trim() || null,
      template_id: form.template_id === '' ? null : Number(form.template_id)
    })
  }

  const templates = templateData?.templates ?? []

  return (
    <Area>
      <Heading subText='自動で送られない相手に、1件だけ依頼します。一覧では「個別に依頼」と分けて表示されます。'>
        個別に依頼する
      </Heading>
      <AreaContent>
        <div className='grid gap-4 md:grid-cols-2'>
          <label className='flex flex-col gap-1'>
            <Text size={12}>送信先のメールアドレス（必須）</Text>
            <input
              className={input}
              value={form.customer_email}
              placeholder='例）tanaka@example.co.jp'
              onChange={(e) => patch({ customer_email: e.target.value })}
            />
          </label>
          <label className='flex flex-col gap-1'>
            <Text size={12}>お名前</Text>
            <input
              className={input}
              value={form.customer_name}
              placeholder='例）田中'
              onChange={(e) => patch({ customer_name: e.target.value })}
            />
          </label>
          <label className='flex flex-col gap-1'>
            <Text size={12}>商品ID</Text>
            <input
              className={input}
              value={form.product_external_id}
              placeholder='例）SKU001'
              onChange={(e) => patch({ product_external_id: e.target.value })}
            />
          </label>
          <label className='flex flex-col gap-1'>
            <Text size={12}>商品名</Text>
            <input
              className={input}
              value={form.product_name}
              placeholder='メールの本文に差し込まれます'
              onChange={(e) => patch({ product_name: e.target.value })}
            />
          </label>
          <label className='flex flex-col gap-1'>
            <Text size={12}>受注ID</Text>
            <input
              className={input}
              value={form.order_id}
              placeholder='分かる場合のみ'
              onChange={(e) => patch({ order_id: e.target.value })}
            />
          </label>
          <label className='flex flex-col gap-1'>
            <Text size={12}>使う文面</Text>
            <select
              className={input}
              value={form.template_id}
              onChange={(e) => patch({ template_id: e.target.value })}
            >
              <option value=''>既定の文面</option>
              {templates.map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {t.template_name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className='mt-4 flex flex-wrap items-center gap-3'>
          <Button
            color={ButtonColor.SECONDARY}
            loading={isMutating}
            disabled={isMutating}
            onClick={onSubmit}
          >
            依頼を作る
          </Button>
          {error && (
            <Text size={12} color={TextColor.CRITICAL}>
              {error}
            </Text>
          )}
        </div>

        {result && (
          <div className='mt-4 flex flex-col gap-2 rounded-lg border border-border-primary px-4 py-3'>
            {result.note && (
              <Text size={12} color={TextColor.SECONDARY}>
                {result.note}
              </Text>
            )}
            {result.subject && (
              <Text size={12} color={TextColor.SECONDARY}>
                件名: {result.subject}
              </Text>
            )}
            {result.form_url && (
              <div className='flex flex-wrap items-center gap-2'>
                <Text size={12} color={TextColor.SECONDARY}>
                  投稿フォームのURL
                </Text>
                <code className='text-size-10 min-w-0 flex-1 overflow-x-auto rounded bg-fill-secondary px-2 py-1'>
                  {result.form_url}
                </code>
                <Button
                  color={ButtonColor.TERTIARY}
                  onClick={() => {
                    navigator.clipboard
                      .writeText(result.form_url ?? '')
                      .then(() => setCopied(true))
                      .catch(() => setCopied(false))
                  }}
                >
                  {copied ? 'コピーしました' : 'URLをコピー'}
                </Button>
              </div>
            )}
          </div>
        )}
      </AreaContent>
    </Area>
  )
}

export default ManualRequestPanel
